import React, { useState, useEffect } from 'react';
import { servicesService } from '../../services/servicesService';
import { getServiceCategoryIcon } from '../../config/serviceCategoryIcons';
import { US_STATES } from '../../constants/usStates';
import './ServiceCreateModal.css';

/**
 * ServiceCreateModal - Modal for creating new service posts
 * Allows authenticated members to post services they offer or need
 */
function ServiceCreateModal({ config, memberInfo, serviceType, onClose, onSuccess, onAuthRequired }) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('');
  const [urgency, setUrgency] = useState('normal');
  const [neededBy, setNeededBy] = useState('');
  const [isVolunteer, setIsVolunteer] = useState(false);
  const [serviceCity, setServiceCity] = useState('');
  const [serviceState, setServiceState] = useState('');
  const [locationNote, setLocationNote] = useState('');
  const [saveLocationToProfile, setSaveLocationToProfile] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Debug: Log serviceType when modal opens
  useEffect(() => {
    console.log('[ServiceCreateModal] Opened with serviceType:', serviceType);
  }, [serviceType]);

  // Pre-populate location from member profile
  useEffect(() => {
    if (memberInfo?.city) {
      setServiceCity(memberInfo.city);
    }
    if (memberInfo?.state) {
      setServiceState(memberInfo.state);
    }
  }, [memberInfo]);

  // Service categories
  const categories = [
    'Transportation',
    'Home Repair',
    'Technology Help',
    'Education/Tutoring',
    'Healthcare Support',
    'Shopping/Errands',
    'Childcare',
    'Elder Care',
    'Professional Services',
    'Religious Services',
    'Other'
  ];

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // If no member info, trigger auth flow
      if (!memberInfo || !memberInfo.id) {
        if (onAuthRequired) {
          onClose();
          onAuthRequired();
        } else {
          setError('Please sign in to post a service');
        }
        setLoading(false);
        return;
      }

      // Validate required fields
      if (!title.trim() || !description.trim() || !category) {
        setError('Please fill in all required fields');
        setLoading(false);
        return;
      }

      // Additional validation for services needed
      if (serviceType === 'needed' && !neededBy) {
        setError('Please specify when this service is needed');
        setLoading(false);
        return;
      }

      // Validate location for services needed
      if (serviceType === 'needed' && (!serviceCity.trim() || !serviceState)) {
        setError('Please provide location (city and state) for services needed');
        setLoading(false);
        return;
      }

      // Prepare service data
      const serviceData = {
        title: title.trim(),
        description: description.trim(),
        category,
        service_type: serviceType,
        urgency: serviceType === 'needed' ? urgency : null,
        needed_by: serviceType === 'needed' ? neededBy : null,
        is_volunteer: isVolunteer,
        center_id: config.center_id,
        member_id: memberInfo?.id,
        service_city: serviceCity.trim() || null,
        service_state: serviceState || null,
        location_note: locationNote.trim() || null,
        save_location_to_profile: saveLocationToProfile
        // Note: status is set by backend based on member's membership_status
      };

      // Submit to service
      const result = await servicesService.createService(serviceData);

      if (result.success) {
        // Pass context to success handler for context-aware messaging
        onSuccess({
          ...result.service,
          _context: {
            membership_status: result.membership_status,
            service_status: result.service?.status,
            pending_reason: result.pending_reason,
            auto_approved: result.auto_approved
          }
        });
      } else {
        setError(result.error || 'Failed to create service post');
      }
    } catch (err) {
      setError('An error occurred. Please try again.');
      console.error('Create service error:', err);
    } finally {
      setLoading(false);
    }
  };

  // Format date for input (today's date as minimum)
  const getMinDate = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content modal-large" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>
            {serviceType === 'offered' ? 'Offer a Service' : 'Request a Service'}
          </h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        <div className="modal-body">
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label>Service Title *</label>
              <input
                type="text"
                placeholder={serviceType === 'offered' 
                  ? "e.g., 'Grocery Shopping Assistance'" 
                  : "e.g., 'Need Help Moving Furniture'"}
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                required
                className="form-input"
                maxLength="100"
              />
              <p className="form-hint">
                A brief, clear title for your {serviceType === 'offered' ? 'offer' : 'request'}
              </p>
            </div>

            <div className="form-group">
              <label>Category *</label>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                required
                className="form-input"
              >
                <option value="">Select a category...</option>
                {categories.map(cat => {
                  const categoryConfig = getServiceCategoryIcon(cat);
                  return (
                    <option key={cat} value={cat}>
                      {categoryConfig.icon ? '● ' : ''}{cat}
                    </option>
                  );
                })}
              </select>
              <p className="form-hint">
                Choose the category that best describes your service
              </p>
            </div>

            <div className="form-group">
              <label>Description *</label>
              <textarea
                placeholder={serviceType === 'offered'
                  ? "Describe what you can help with, your availability, and any relevant experience..."
                  : "Describe what you need help with, any specific requirements..."}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                required
                className="form-input form-textarea"
                rows="4"
                maxLength="500"
              />
              <p className="form-hint">
                {description.length}/500 characters
              </p>
            </div>

            {/* Volunteer Service Checkbox - Only for Services Offered */}
            {serviceType === 'offered' && (
              <div className="form-group">
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '5px' }}>
                  <input
                    type="checkbox"
                    id="volunteer-service"
                    checked={isVolunteer}
                    onChange={(e) => setIsVolunteer(e.target.checked)}
                    style={{ width: '18px', height: '18px', cursor: 'pointer', flexShrink: 0 }}
                  />
                  <label
                    htmlFor="volunteer-service"
                    style={{ cursor: 'pointer', fontSize: '0.95rem', margin: 0, color: '#333' }}
                  >
                    This is a free/volunteer service
                  </label>
                </div>
              </div>
            )}

            {/* Additional fields for services needed */}
            {serviceType === 'needed' && (
              <>
                <div className="form-group">
                  <label>When Needed By *</label>
                  <input
                    type="date"
                    value={neededBy}
                    onChange={(e) => setNeededBy(e.target.value)}
                    required
                    className="form-input"
                    min={getMinDate()}
                  />
                  <p className="form-hint">
                    The date by which you need this service
                  </p>
                </div>

                <div className="form-group">
                  <label>Urgency Level *</label>
                  <div className="urgency-options">
                    <label className="radio-label">
                      <input
                        type="radio"
                        name="urgency"
                        value="normal"
                        checked={urgency === 'normal'}
                        onChange={(e) => setUrgency(e.target.value)}
                      />
                      <span className="urgency-normal">Normal</span>
                      <small>Can wait a few days</small>
                    </label>
                    <label className="radio-label">
                      <input
                        type="radio"
                        name="urgency"
                        value="urgent"
                        checked={urgency === 'urgent'}
                        onChange={(e) => setUrgency(e.target.value)}
                      />
                      <span className="urgency-urgent">Urgent</span>
                      <small>Need within 24-48 hours</small>
                    </label>
                    <label className="radio-label">
                      <input
                        type="radio"
                        name="urgency"
                        value="emergency"
                        checked={urgency === 'emergency'}
                        onChange={(e) => setUrgency(e.target.value)}
                      />
                      <span className="urgency-emergency">Emergency</span>
                      <small>Need immediately</small>
                    </label>
                  </div>
                </div>

                <div className="form-group">
                  <label>Service Location *</label>
                  <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '10px' }}>
                    <input
                      type="text"
                      placeholder="City"
                      value={serviceCity}
                      onChange={(e) => setServiceCity(e.target.value)}
                      required
                      className="form-input"
                    />
                    <select
                      value={serviceState}
                      onChange={(e) => setServiceState(e.target.value)}
                      required
                      className="form-input"
                    >
                      <option value="">State</option>
                      {US_STATES.map(state => (
                        <option key={state.value} value={state.value}>
                          {state.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <p className="form-hint">
                    Where is this service needed?
                  </p>
                </div>

                <div className="form-group">
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '5px' }}>
                    <input
                      type="checkbox"
                      id="save-location"
                      checked={saveLocationToProfile}
                      onChange={(e) => setSaveLocationToProfile(e.target.checked)}
                      style={{ width: '18px', height: '18px', cursor: 'pointer', flexShrink: 0 }}
                    />
                    <label
                      htmlFor="save-location"
                      style={{ cursor: 'pointer', fontSize: '0.95rem', margin: 0, color: '#333' }}
                    >
                      Save this location to my profile for future posts
                    </label>
                  </div>
                </div>
              </>
            )}

            {error && <div className="error-message">{error}</div>}

            {memberInfo && (
              <div className="contact-info-box">
                <p>
                  <strong>Your Contact:</strong> {memberInfo.name || 'Member'} {memberInfo.last_initial || ''}.
                </p>
                <p className="contact-hint">
                  Community members will be able to contact you via WhatsApp when they view your post.
                  Your phone number remains private.
                </p>
              </div>
            )}

            <div className="modal-footer">
              <button type="button" className="btn-secondary" onClick={onClose}>
                Cancel
              </button>
              <button type="submit" className="btn-primary" disabled={loading}>
                {loading ? 'Posting...' : `Post ${serviceType === 'offered' ? 'Offer' : 'Request'}`}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default ServiceCreateModal;